import { Constantes } from "@/ts/constantes";
import { notificacion, notificacionError } from "@/components/Alerts";
import { createColumnHelper, tableFeatures } from "@tanstack/react-table";
import { TypedTable } from "@/components/ui/table/TypedTable";
import { useStockAlmacen, useSyncArticulo, type DBPrioritaria, type LineaStocksArticulo } from "./SincroAlmacen";
import { CommonFeatures } from "@/hooks/useTanstackTable";
import { useMemo, useState } from "react";
import { ModalConfirmar } from "@/components/modals/Confirmar";

interface TablaStockArticulosProps {
	readonly idAlmacen: number;
	readonly syncFilter: "todos" | "no-sincronizados";
	readonly stock: "conStock" | "todos";
}
export function TablaStockArticulos(props: TablaStockArticulosProps) {
	const { data, error, isPending } = useStockAlmacen(props.idAlmacen);

	const { columns, features } = useMemo(() => Columns(props.idAlmacen), [props.idAlmacen]);

	const dataFiltrado = useMemo(() => {
		let finalData = data;

		if (props.syncFilter === "no-sincronizados")
			finalData = finalData?.filter(line => line.stockERP != line.stockMySQL);

		if (props.stock === "conStock")
			finalData = finalData?.filter(line => (line.stockERP ?? 0) != 0 || (line.stockMySQL ?? 0) != 0);

		return finalData;
	}, [data, props.syncFilter, props.stock]);

	return (
		<TypedTable
			className="dataTable display"
			columns={columns}
			features={features}
			data={dataFiltrado ?? []}

			globalSearch
			pagination
			rowClassName={(line) => {
				if (line.stockERP == line.stockMySQL) {
					return "sincronizado";
				} else {
					return "no-sincronizado";
				}
			}}

			noResultsMessage="No hay entradas"
			errorMessage="Error al cargar los datos"
			isLoading={isPending}
			isError={error != null}
		/>
	);
}
function BtnSincro(props: { readonly tipo: DBPrioritaria, readonly rowData: LineaStocksArticulo, readonly idAlmacen: number; }) {
	const { tipo, rowData, idAlmacen } = props;
	const syncArticulo = useSyncArticulo();
	const [showConfirm, setShowConfirm] = useState(false);

	const onConfirmar = () => {
		syncArticulo.mutate(
			{
				prioritaria: tipo,
				idArticulo: rowData.idArticulo,
				lote: rowData.lote,
				idAlmacen: idAlmacen,
			},
			{
				onError: (error) => {
					notificacionError(error.message);
				},
				onSuccess: () => {
					setShowConfirm(false);
					notificacion("Sincronizado correctamente");
				}
			}
		);
	};


	return (
		<div style={{ display: "flex", justifyContent: "center" }}>
			<button type="button" className="main-color btnIcon" onClick={(event) => {
				event.preventDefault();
				setShowConfirm(true);
			}}>
				<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="img" height="30px" viewBox="0 0 16 16">
					<g fill="white">
						<path d="M7.84 4.1a.178.178 0 0 1 .32 0l.634 1.285a.18.18 0 0 0 .134.098l1.42.206c.145.021.204.2.098.303L9.42 6.993a.18.18 0 0 0-.051.158l.242 1.414a.178.178 0 0 1-.258.187l-1.27-.668a.18.18 0 0 0-.165 0l-1.27.668a.178.178 0 0 1-.257-.187l.242-1.414a.18.18 0 0 0-.05-.158l-1.03-1.001a.178.178 0 0 1 .098-.303l1.42-.206a.18.18 0 0 0 .134-.098z" />
						<path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v13.5a.5.5 0 0 1-.777.416L8 13.101l-5.223 2.815A.5.5 0 0 1 2 15.5zm2-1a1 1 0 0 0-1 1v12.566l4.723-2.482a.5.5 0 0 1 .554 0L13 14.566V2a1 1 0 0 0-1-1z" />
					</g>
				</svg>
			</button>

			<ModalConfirmar
				show={showConfirm}
				close={() => setShowConfirm(false)}
				message={`¿Está seguro de que desea sincronizar el artículo ${rowData.idArticulo}-${rowData.nombreArticulo} con preferencia en ${tipo == "EKON" ? "EKON" : "MRP"}?`}
				onConfirm={onConfirmar}
			/>
		</div>
	);
}


function Columns(idAlmacen: number) {
	const columnHelper = createColumnHelper<typeof features, LineaStocksArticulo>();
	const features = tableFeatures(CommonFeatures);

	const getBtnEKON = (row: LineaStocksArticulo) => <BtnSincro tipo="EKON" rowData={row} idAlmacen={idAlmacen} />;
	const getBtnMySQL = (row: LineaStocksArticulo) => <BtnSincro tipo="MYSQL" rowData={row} idAlmacen={idAlmacen} />;


	const columns = columnHelper.columns([
		columnHelper.accessor(row => `${row.idArticulo} - ${row.nombreArticulo}`, { header: "Articulo", meta: { className: "dt-center", style: { width: "30%" } } }),
		columnHelper.accessor("lote", { header: "Lote", meta: { className: "dt-center", style: { width: "10%" }, hidden: !Constantes.existenLotes } }),

		columnHelper.accessor("stockERP", { header: "Cantidad ERP", meta: { className: "dt-right", style: { width: "15%" } }, cell: info => info.getValue() ?? "-" }),
		columnHelper.display({ id: "sincroEkon", header: "", meta: { className: "dt-left", style: { width: "15%" } }, cell: ({ row }) => getBtnEKON(row.original) }),

		columnHelper.accessor("stockMySQL", { header: "Cantidad Almacén", meta: { className: "dt-right", style: { width: "15%" } }, cell: info => info.getValue() ?? "-" }),
		columnHelper.display({ id: "sincroMysql", header: "", meta: { className: "dt-left", style: { width: "15%" } }, cell: ({ row }) => getBtnMySQL(row.original) })
	]);
	return { columns, features };
}